// The Activity tab's filters and rows, pure (activity.selftest.ts runs
// them). Filters live in the URL beside the tab
// (`?tab=activity&q=momentum&status=attention&kind=job&workflow=<slug>`) so
// a card's "Runs", a job link or a bookmark opens the list already
// narrowed: one search box (the name, the second line, who asked), a
// status chip (All · Live · Needs attention · Finished; a cancelled run
// shows only under All), and two removable narrowings, "Team tasks" and a
// workflow. A row is a run with, for a job, its job (title, who asked);
// its second line says what it came to (its outcome once finished) or
// which settings it ran with. Also: where a row opens (a job's own page,
// else the run view, on a phone too).

import type { JobSummary } from "@/api/jobs";
import { isLiveRunStatus, type RunSummary } from "@/api/workflows";
import { humanizeSlug } from "@/lib/format";

import { awaitingApproval } from "../jobs/jobModel";
import { runElapsed } from "../model";
import { jobPath, workflowRunPath } from "../paths";
import { publishedKeyName, viaKey } from "../publish/publishModel";

export type ActivityStatus = "all" | "live" | "attention" | "finished";

export interface ActivityFilters {
  /** Free text: the name, the second line, who asked. */
  q: string;
  status: ActivityStatus;
  /** "job" = Team tasks only (a job is a "Team task" in the UI). */
  kind: "" | "job";
  /** A workflow's slug; "" = any. */
  workflow: string;
}

export const NO_ACTIVITY_FILTERS: ActivityFilters = { q: "", status: "all", kind: "", workflow: "" };

/** The query parameters the filters use (leaving the tab clears them). */
export const RUN_FILTER_PARAMS = ["q", "status", "kind", "workflow"] as const;

const STATUSES: readonly ActivityStatus[] = ["all", "live", "attention", "finished"];
/** Links written before the chips (?status=done|failed|stopped). */
const OLD_STATUS: Record<string, ActivityStatus> = { done: "finished", failed: "attention", stopped: "all" };

export const STATUS_CHIPS: { value: ActivityStatus; label: string }[] = [
  { value: "all", label: "All" },
  { value: "live", label: "Live" },
  { value: "attention", label: "Needs attention" },
  { value: "finished", label: "Finished" },
];

export function filtersFromParams(params: URLSearchParams): ActivityFilters {
  const raw = params.get("status") ?? "all";
  const status = (STATUSES as readonly string[]).includes(raw) ? (raw as ActivityStatus) : (OLD_STATUS[raw] ?? "all");
  return {
    q: params.get("q") ?? "",
    status,
    kind: params.get("kind") === "job" ? "job" : "",
    workflow: params.get("workflow")?.trim() ?? "",
  };
}

/** `params` with the filters written in (an "any" filter leaves no parameter). */
export function withFilterParams(params: URLSearchParams, f: ActivityFilters): URLSearchParams {
  const next = new URLSearchParams(params);
  const values: Record<(typeof RUN_FILTER_PARAMS)[number], string> = { q: f.q, status: f.status === "all" ? "" : f.status, kind: f.kind, workflow: f.workflow };
  for (const key of RUN_FILTER_PARAMS) {
    if (values[key]) next.set(key, values[key]);
    else next.delete(key);
  }
  return next;
}

/**
 * The Activity tab's query for a link written before it (?tab=runs, with
 * its filters, or ?tab=jobs, which opens it with "Team tasks" only).
 */
export function activitySearchFor(params: URLSearchParams): string {
  const next = new URLSearchParams(params);
  if (next.get("tab") === "jobs") next.set("kind", "job");
  next.set("tab", "activity");
  const status = next.get("status");
  if (status && OLD_STATUS[status]) {
    if (OLD_STATUS[status] === "all") next.delete("status");
    else next.set("status", OLD_STATUS[status]);
  }
  return `?${next}`;
}

// ─── rows ───

/** One row: a run, and for a job run its job. */
export interface ActivityRow {
  run: RunSummary;
  job: JobSummary | null;
  /** Line 1: the workflow's name, or the job's full title. */
  title: string;
  /** Line 2, quiet: "Team task · Julia", or a run's key setting (after "via <key name>" for a run a key started). */
  line: string;
  /** "You", the agent who started it, or "via <key name>". */
  who: string;
}

const FINISHED = new Set(["completed", "completed_with_failures"]);

export const isFinishedRun = (status: string) => FINISHED.has(status);

/** One line of text, cut at `max` characters on a word where it can be. */
export function cutLine(text: string, max: number): string {
  const one = text.replace(/\s+/g, " ").trim();
  if (one.length <= max) return one;
  const cut = one.slice(0, max);
  const space = cut.lastIndexOf(" ");
  return `${(space > max * 0.6 ? cut.slice(0, space) : cut).replace(/[\s,;:.–-]+$/, "")}…`;
}

/** A list as "SPY, QQQ" or "SPY +11 more". */
export function listWords(values: readonly unknown[]): string {
  const items = values.map((v) => String(v).trim()).filter(Boolean);
  if (items.length <= 2) return items.join(", ");
  return `${items[0]} +${items.length - 1} more`;
}

/** Settings that say what a run is about, whatever else it was given. */
const SUBJECT_KEYS = ["question", "focus", "topic", "subject", "product_name", "product", "channel", "area", "paper"];

/**
 * The setting that tells runs of one workflow apart, cut to 120 characters
 * (the row cuts it again to its width): a subject setting (the question, the
 * product), else the first list under its name ("Symbols: SPY +11 more"),
 * else the first long text. "" when none.
 */
export function keySetting(params: Record<string, unknown> | null | undefined): string {
  const named = SUBJECT_KEYS.map((k) => params?.[k]).find((v): v is string => typeof v === "string" && v.trim() !== "");
  if (named) return cutLine(named, 120);
  const entries = Object.entries(params ?? {});
  const list = entries.find((e): e is [string, unknown[]] => Array.isArray(e[1]) && e[1].length > 0);
  if (list) return `${humanizeSlug(list[0])}: ${listWords(list[1])}`;
  const values = entries.map(([, v]) => v);
  const text = values.find((v): v is string => typeof v === "string" && v.trim().length > 20 && /\s/.test(v.trim()));
  return text ? cutLine(text, 120) : "";
}

/** "You" for the dashboard, "Schedule" for the engine's timetable, "via <name>" for a published workflow's key, else the agent's name. */
export function startedByWho(startedBy: string | null | undefined, nameOf: (template: string) => string): string {
  if (!startedBy || startedBy === "operator") return "You";
  const key = publishedKeyName(startedBy);
  if (key !== null) return viaKey(key);
  return startedBy === "schedule" ? "Schedule" : nameOf(startedBy);
}

/**
 * A run's second line: what it ran on (keySetting), never its outcome. A list
 * is for finding a run; what it found is on its page (2026-10-11: the
 * conclusions, two lines each and most opening with a verdict word, made the
 * list a wall of text).
 */
export function runLine(run: Pick<RunSummary, "params">): string {
  return keySetting(run.params);
}

/** A job's second line: "Team task · Julia". What happens next is the status badge's word ("Awaiting approval"), not said twice. */
export function jobLine(who: string): string {
  return ["Team task", who].filter(Boolean).join(" · ");
}

/** A run's Took: "—" while a plan waits for approval (nothing has run), "25 min so far" (live) while it runs, else how long it took. */
export function tookCell(run: Pick<RunSummary, "status" | "job" | "started_at" | "ended_at">, now: Date): { text: string; live: boolean } {
  if (run.job && awaitingApproval(run.job.status, run.status)) return { text: "", live: false };
  const took = runElapsed(run, now);
  if (!took) return { text: "", live: false };
  return isLiveRunStatus(run.status) ? { text: `${took} so far`, live: true } : { text: took, live: false };
}

/** The rows, newest first as the runs came, each job run joined with its job (by run id). */
export function activityRows(runs: readonly RunSummary[], jobs: readonly JobSummary[], nameOf: (template: string) => string): ActivityRow[] {
  const byRun = new Map(jobs.map((j) => [j.run.run_id, j]));
  return runs.map((run) => {
    const job = run.job ? (byRun.get(run.run_id) ?? null) : null;
    const who = startedByWho(job?.requester || run.started_by, nameOf);
    const title = (job?.title || run.workflow_name || "").trim() || "Untitled";
    // a run a published workflow's key started says so first ("via Acme bot · <what it ran on>")
    const line = run.job ? jobLine(who) : publishedKeyName(run.started_by) !== null ? [who, runLine(run)].filter(Boolean).join(" · ") : runLine(run);
    return { run, job, title, who, line };
  });
}

// ─── filtering ───

/** A job run: the Runs list's mark, or a job joined to it. */
const isJob = (row: ActivityRow) => !!row.run.job;

/** Failed, done with failures, waiting for approval, or a job whose checks failed. */
export function needsAttention(run: Pick<RunSummary, "status" | "job">): boolean {
  if (run.status === "failed" || run.status === "completed_with_failures") return true;
  if (run.job && awaitingApproval(run.job.status, run.status)) return true;
  return !!run.job && run.status === "completed" && (run.job.failed_checks ?? 0) > 0;
}

export function statusMatches(run: Pick<RunSummary, "status" | "job">, status: ActivityStatus): boolean {
  switch (status) {
    case "all":
      return true;
    case "live":
      return isLiveRunStatus(run.status);
    case "attention":
      return needsAttention(run);
    case "finished":
      return isFinishedRun(run.status);
  }
}

/** Every word of the search in the name, the second line or who asked (any case). */
export function searchMatches(row: ActivityRow, q: string): boolean {
  const words = q.toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) return true;
  const hay = `${row.title} ${row.line} ${row.who} ${row.run.workflow_name}`.toLowerCase();
  return words.every((w) => hay.includes(w));
}

/** Null when nothing is filtered (every row matches). The workflow filter is applied by the server too. */
export function activityPredicate(f: ActivityFilters): ((row: ActivityRow) => boolean) | null {
  if (!f.q.trim() && f.status === "all" && !f.kind && !f.workflow) return null;
  return (row) => (!f.workflow || row.run.workflow === f.workflow) && (!f.kind || isJob(row)) && statusMatches(row.run, f.status) && searchMatches(row, f.q);
}

/** The chips' counts: live runs, and runs that need attention. */
export function statusCounts(runs: readonly Pick<RunSummary, "status" | "job">[]): { live: number; attention: number } {
  return { live: runs.filter((r) => isLiveRunStatus(r.status)).length, attention: runs.filter(needsAttention).length };
}

// ─── links ───

/** A workflow's runs: Activity narrowed to it (a card's "Runs"). */
export function workflowRunsHref(base: string, slug: string): string {
  return `${base}?${withFilterParams(new URLSearchParams("tab=activity"), { ...NO_ACTIVITY_FILTERS, workflow: slug })}`;
}

/**
 * Where a run opens from a list: a job's own page (its plan, steps, checks
 * and Approve; at the plan while it waits for approval), else the run
 * view. Both work at phone width, so a phone opens the same.
 */
export function runOpenHref(base: string, run: Pick<RunSummary, "run_id" | "status" | "job">): string {
  if (!run.job) return workflowRunPath(base, run.run_id);
  return jobPath(base, run.run_id) + (awaitingApproval(run.job.status, run.status) ? "#plan" : "");
}
